/* eslint-disable @typescript-eslint/no-dynamic-delete */
import type { Identifiable } from '~/models/identifiable'
import { ControllerState } from './controllerState'
import type { LocalStateRemove } from './stateChange'

export class ControllerStateRecord<
  Item extends Identifiable,
> extends ControllerState<Item, Record<string, Item>> {
  override getEmptyState(): Record<string, Item> {
    return {}
  }

  override keepLocalChanges(
    changes: Record<string, Item>,
  ): Record<string, Item> {
    if (!this.localChanges.hasChanges()) {
      return changes
    }

    let result = this.handleStateUpdate(changes)
    result = this.handleStateAdd(result)
    result = this.handleStateRemove(result)

    return result
  }

  override handleStateUpdate(
    newState: Record<string, Item>,
  ): Record<string, Item> {
    if (!this.localChanges.hasUpdateChanges()) {
      return newState
    }

    const keys = Object.keys(newState)
    const result: Record<string, Item> = {}
    for (const key of keys) {
      const change = newState[key]
      const localChange = this.localChanges.getStateUpdate(change)

      if (localChange === undefined) {
        result[key] = change
        continue
      }

      if (localChange.isDifferent(change)) {
        result[key] = localChange.value
        continue
      }

      this.localChanges.removeStateChange(localChange)

      result[key] = change
    }

    return result
  }

  override handleStateAdd(
    newState: Record<string, Item>,
  ): Record<string, Item> {
    if (!this.localChanges.hasAddChanges()) {
      return newState
    }

    for (const item of Object.values(newState)) {
      const localChange = this.localChanges.getStateAdd(item)
      if (localChange !== undefined && !localChange.isDifferent(item)) {
        this.localChanges.removeStateChange(localChange)
      }
    }

    for (const localChange of this.localChanges.getStateAddEntities()) {
      newState[localChange.id] = localChange.getValue()
    }

    return newState
  }

  override handleStateRemove(
    newState: Record<string, Item>,
  ): Record<string, Item> {
    if (!this.localChanges.hasRemoveChanges()) {
      return newState
    }

    const activeLocalChanges: LocalStateRemove<Item>[] = []
    for (const item of Object.values(newState)) {
      const localChange = this.localChanges.getStateRemove(item)
      if (localChange !== undefined && !localChange.isDifferent(item)) {
        delete newState[localChange.id]
        activeLocalChanges.push(localChange)
      }
    }

    this.localChanges.removeInactiveStateChanges(activeLocalChanges)

    return newState
  }

  override applyLocalChanges() {
    const keys = Object.keys(this.data)
    for (const key of keys) {
      const item = this.data[key]
      const localChange = this.localChanges.getStateUpdate(item)

      if (localChange === undefined) {
        return
      }

      this.data[key] = localChange.value
    }

    for (const localChange of this.localChanges.getStateAddEntities()) {
      this.data[localChange.id] = localChange.getValue()
    }

    for (const item of Object.values(this.data)) {
      const localChange = this.localChanges.getStateRemove(item)
      if (localChange) {
        delete this.data[localChange.id]
      }
    }
  }
}
